import { useEffect, useRef } from 'react';
import { requestPortalProject, type PortalProjectDetail } from '@altios/api-client';

const LIVE_REFRESH_MS = 15_000;

export function usePortalLiveProject({
  projectId,
  email,
  enabled,
  onProject
}: {
  projectId: string | null | undefined;
  email: string;
  enabled: boolean;
  onProject: (detail: PortalProjectDetail) => void;
}) {
  const refreshing = useRef(false);
  const onProjectRef = useRef(onProject);

  useEffect(() => {
    onProjectRef.current = onProject;
  }, [onProject]);

  useEffect(() => {
    if (!enabled || !projectId || !email.trim()) return;

    let disposed = false;

    const refresh = async () => {
      if (refreshing.current || disposed) return;
      refreshing.current = true;

      try {
        const detail = await requestPortalProject(projectId, email.trim());
        if (!disposed) onProjectRef.current(detail);
      } catch {
        // Keep the current customer view intact on transient refresh failures.
      } finally {
        refreshing.current = false;
      }
    };

    const onFocus = () => void refresh();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void refresh();
    };

    const timer = window.setInterval(() => void refresh(), LIVE_REFRESH_MS);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      disposed = true;
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [email, enabled, projectId]);
}
